/**
 * Client-side deal suggestions for QuickBooks payments/expenses.
 * Mirrors the server rematch heuristics (customer name + fuzzy title in text).
 */

import { EnhancedDeal } from '../types';
import { getProjectDisplayName } from './projectName';

export interface QbDealSuggestionContext {
  vendorName?: string;
  description?: string;
  customerName?: string;
  lineItems?: Array<{ customerName?: string | null; description?: string }>;
}

export interface QbDealSuggestion {
  dealId: string;
  score: number;
  reason: string;
}

function normalizeText(s: string): string {
  return s.toLowerCase().replace(/\s+/g, ' ').trim();
}

function namesLooselyMatch(a: string, b: string): boolean {
  const x = normalizeText(a);
  const y = normalizeText(b);
  if (!x || !y) return false;
  return x === y || x.includes(y) || y.includes(x);
}

/** Rank won deals that best match a QB transaction's vendor, memo, and customer fields. */
export function rankDealsForQbTransaction(
  deals: EnhancedDeal[],
  ctx: QbDealSuggestionContext,
  limit = 5,
): QbDealSuggestion[] {
  const haystack = normalizeText(
    [
      ctx.vendorName,
      ctx.description,
      ctx.customerName,
      ...(ctx.lineItems || []).flatMap(li => [li.customerName, li.description]),
    ]
      .filter(Boolean)
      .join(' '),
  );

  const scores: QbDealSuggestion[] = [];

  for (const deal of deals) {
    if (!deal.id) continue;
    let score = 0;
    let reason = '';

    const displayName = getProjectDisplayName(deal) || deal.title || '';
    const contact = deal.contactName || '';
    const keys = [
      displayName,
      deal.title,
      deal.projectNumber,
      deal.clientLabel,
      deal.descriptor,
    ]
      .filter((k): k is string => Boolean(k && String(k).trim().length > 2))
      .map(k => normalizeText(String(k)));

    if (ctx.customerName && contact && namesLooselyMatch(ctx.customerName, contact)) {
      score += 80;
      reason = `Customer "${contact}"`;
    }

    for (const li of ctx.lineItems || []) {
      if (li.customerName && contact && namesLooselyMatch(li.customerName, contact)) {
        score += 75;
        if (!reason) reason = `Line customer "${contact}"`;
        break;
      }
    }

    for (const key of keys) {
      if (key.length <= 3) continue;
      if (haystack.includes(key)) {
        score += 55 + Math.min(key.length, 25);
        if (!reason) reason = `Text matches "${displayName || deal.title}"`;
        break;
      }
    }

    const num = deal.projectNumber?.replace(/^#/, '').trim();
    if (num && num.length >= 3 && haystack.includes(normalizeText(num))) {
      score += 45;
      if (!reason) reason = `Project #${num}`;
    }

    if (score > 0) {
      scores.push({ dealId: deal.id, score, reason });
    }
  }

  return scores.sort((a, b) => b.score - a.score).slice(0, limit);
}

export function suggestedDealIdsForQbTransaction(
  deals: EnhancedDeal[],
  ctx: QbDealSuggestionContext,
  limit = 5,
): string[] {
  return rankDealsForQbTransaction(deals, ctx, limit).map(s => s.dealId);
}
